import type { SpotSceneResponse } from "../types/spot-scene-response.gen";
import {
  getConfiguredCmsOrigin,
  shouldUseCmsDevProxy,
} from "./cms-origin";
import { getCurrentToken, getMode, refreshNow } from "./session-token.js";

export type { SpotSceneResponse };

const CMS_PROXY_PREFIX = "/__cms";

function sceneApiBase(): string {
  if (shouldUseCmsDevProxy()) {
    return `${CMS_PROXY_PREFIX}/api/spot`;
  }
  return `${getConfiguredCmsOrigin()}/api/spot`;
}

/**
 * Laedt die Scene-Konfiguration vom CMS.
 *
 * `mode=draft` (von Editor-Play-Button gesetzt) waehlt `/scene/draft`
 * mit Bearer-Token; sonst wird der oeffentliche `/scene`-Endpoint genutzt.
 *
 * Token-Quelle: das zentrale `session-token`-Modul, das periodisch refreshed.
 * Bei einem 401 (z.B. nach langem Tab-Sleep) erzwingen wir einen Refresh
 * und versuchen den Request einmal erneut.
 */
export async function requestSceneConfig(
  spotId: string
): Promise<SpotSceneResponse> {
  const mode = getMode();
  const path = mode === "draft" ? "/scene/draft" : "/scene";
  const url = `${sceneApiBase()}/${encodeURIComponent(spotId)}${path}`;

  const buildHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {};
    const token = getCurrentToken();
    if (mode === "draft" && token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  };

  const fetchCurrentScene = () => fetch(url, {
    cache: "no-store",
    headers: buildHeaders(),
  });

  let response = await fetchCurrentScene();
  if (response.status === 401 && mode === "draft") {
    const refreshed = await refreshNow();
    if (refreshed) {
      response = await fetchCurrentScene();
    }
  }
  if (!response.ok) {
    throw new Error(`Scene graph request failed: ${response.status}`);
  }
  return (await response.json()) as SpotSceneResponse;
}
